import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { cartao, cor, texto } from '#components/finapp/estilo';
import type { MediaDeCategoria } from '#components/finapp/motor/medias';
import { ID_SEM_CATEGORIA_MEDIA } from '#components/finapp/motor/medias';
import type { ChaveDeTipo } from '#components/finapp/motor/tiposDeGasto';
import {
  NAO_CLASSIFICADO,
  ROTULO_SEM_TIPO,
} from '#components/finapp/motor/tiposDeGasto';

import { LinhaDeMedia } from './LinhaDeMedia';

type Props = {
  tipo: ChaveDeTipo;
  mediaDoTipo: number;
  categorias: MediaDeCategoria[];
  /** categoriaId → gasto do mês corrente, em centavos, positivo. */
  esteMesPorCategoria: Map<string, number>;
  /** Celular. Repassado direto para cada `LinhaDeMedia` — ver o JSDoc lá. */
  estreito?: boolean;
};

const ROTULOS: Record<ChaveDeTipo, string> = {
  essencial: 'Essencial',
  luxo: 'Luxo',
  investimento: 'Investimento',
  [NAO_CLASSIFICADO]: ROTULO_SEM_TIPO,
};

/**
 * ⚠️⚠️ Este bloco é renderizado MESMO VAZIO quando o tipo é
 * `NAO_CLASSIFICADO`. É a mitigação de ter tirado a fila de Pendências do
 * menu: categoria faltando envenena a média em silêncio, e este balde é o
 * único aviso que sobrou. Os outros três somem quando zerados — só este não.
 */
export function BlocoDeTipo({
  tipo,
  mediaDoTipo,
  categorias,
  esteMesPorCategoria,
  estreito = false,
}: Props) {
  const { t } = useTranslation();
  const ehNaoClassificado = tipo === NAO_CLASSIFICADO;

  if (categorias.length === 0 && !ehNaoClassificado) return null;

  /**
   * ⚠️⚠️ Revisão final da branch (Finding 10, Minor) — o balde
   * `NAO_CLASSIFICADO` recolhe DUAS situações diferentes: categoria SEM
   * TIPO (se resolve em Ajustes ▸ Tipos de gasto) e transação SEM CATEGORIA
   * NENHUMA (`category: null`, o caso dominante da importação automática do
   * banco — ver `ID_SEM_CATEGORIA_MEDIA` em `motor/medias.ts`), que aquela
   * tela NÃO resolve — lá só existem categorias, não transações soltas. A
   * frase antiga mandava sempre para Ajustes, mesmo quando isso não ajuda.
   */
  const temSemCategoria = categorias.some(
    c => c.categoriaId === ID_SEM_CATEGORIA_MEDIA,
  );
  const temCategoriaSemTipo = categorias.some(
    c => c.categoriaId !== ID_SEM_CATEGORIA_MEDIA,
  );
  const avisoNaoClassificado =
    temCategoriaSemTipo && temSemCategoria
      ? t(
          'Parte destes gastos tem categoria sem tipo (ajuste em Ajustes ▸ Tipos de gasto) e parte não tem categoria nenhuma — isso se resolve categorizando a transação, não aqui.',
        )
      : temCategoriaSemTipo
        ? t(
            'Estes gastos têm categoria, mas ela ainda não tem tipo. Ajuste em Ajustes ▸ Tipos de gasto para as médias ficarem certas.',
          )
        : t(
            'Estes gastos não têm categoria nenhuma — comum na importação automática do banco. Categorize a transação primeiro; Ajustes ▸ Tipos de gasto não resolve isso.',
          );

  return (
    <View style={cartao} data-testid={`bloco-tipo-${tipo}`}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <Text style={texto.rotuloCartao}>{t(ROTULOS[tipo])}</Text>
        <Text style={texto.valorCartao}>{integerToCurrency(mediaDoTipo)}</Text>
      </View>

      {categorias.length === 0 ? (
        <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
          {t('Nada aqui — todos os seus gastos estão classificados.')}
        </Text>
      ) : (
        categorias.map(c => (
          <LinhaDeMedia
            key={c.categoriaId}
            media={c}
            esteMes={esteMesPorCategoria.get(c.categoriaId) ?? 0}
            estreito={estreito}
          />
        ))
      )}

      {ehNaoClassificado && categorias.length > 0 && (
        <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
          {avisoNaoClassificado}
        </Text>
      )}
    </View>
  );
}
